import { pick } from "lodash";
import moment from "moment";

import { COMPOSITE_FIELD_DELIMITER } from "./SubmissionForm/Constant";

import { convertUtcToLocal } from "../Common/utils/dateUtils";

export const MAX_PDF_CHAR_SAMPLE_ID = 1000;
export const SAMPLE_SUBMISSION_BASE = "/sample-submission";
export const TEST_DIRECTORY_PATH = `${SAMPLE_SUBMISSION_BASE}/test-directory`;
export const PRODUCT_ENVIRONMENT_CATEGORY = "product&environment";
const CREATE_SAMPLE_SUBMISSION_PATH = `${SAMPLE_SUBMISSION_BASE}/create-sample-submission`;
const VALID_SAMPLE_CATEGORIES = ["product", "environment", PRODUCT_ENVIRONMENT_CATEGORY];

function isValidSampleCategory(category) {
  return VALID_SAMPLE_CATEGORIES.includes(category);
}

export { isValidSampleCategory };

/** Build the create route for a given category (product|environment|product&environment). */
export function buildCreateSampleSubmissionPath(category) {
  if (!isValidSampleCategory(category)) {
    throw new Error(`Invalid sample category: ${category}`);
  }
  return `${CREATE_SAMPLE_SUBMISSION_PATH}/${encodeURIComponent(category)}`;
}

/**
 * Parse pathname only (no invitation). Used when URL alone should decide create vs list.
 * Supports /sample-submission/create-sample-submission/{product|environment},
 * /sample-submission/test-directory, and legacy /sample-submission/{product|environment}.
 * @returns {{ view: "list" }|{ view: "create", category: string }|{ view: "directory" }|{ view: "other" }}
 */
export function parseSampleSubmissionPathFromUrl(pathname) {
  if (!pathname || !pathname.startsWith(SAMPLE_SUBMISSION_BASE)) {
    return { view: "other" };
  }
  const pathSegments = pathname.slice(SAMPLE_SUBMISSION_BASE.length).split("/").filter(Boolean);
  if (pathSegments.length === 0) return { view: "list" };
  const [firstSegment, categorySegment] = pathSegments;
  const decodedFirstSegment = decodeURIComponent(firstSegment);
  const decodedCategorySegment = categorySegment ? decodeURIComponent(categorySegment) : "";
  if (pathSegments.length === 1 && firstSegment === "test-directory") {
    return { view: "directory" };
  }
  if (pathSegments.length === 1 && isValidSampleCategory(decodedFirstSegment)) {
    return { view: "create", category: decodedFirstSegment };
  }
  if (firstSegment === "create-sample-submission" && isValidSampleCategory(decodedCategorySegment)) {
    return { view: "create", category: decodedCategorySegment };
  }
  return { view: "other" };
}

/**
 * View descriptor for sample submission: URL, or third-party invitation when URL is not create.
 * @param {string} pathname
 * @param {{ thirdParty?: boolean, sample_category?: string }} [invitation]
 * @returns {{ view: "list" }|{ view: "create", category: string }|{ view: "directory" }|{ view: "other" }}
 */
export function parseSampleSubmissionPath(pathname, invitation = {}) {
  const fromUrl = parseSampleSubmissionPathFromUrl(pathname);
  if (fromUrl.view === "create" || fromUrl.view === "directory") return fromUrl;
  if (invitation.thirdParty && isValidSampleCategory(invitation.sample_category)) {
    return { view: "create", category: invitation.sample_category };
  }
  return fromUrl;
}

/**
 * Whether a test cannot be performed for the current sample side.
 * Uses product_doable / Environmental_doable. Missing flags are treated as doable.
 */
export function isTestNotDoableForSide(testObj, sampleCategory) {
  if (!testObj || typeof testObj !== "object") return false;
  if (sampleCategory === "environment") {
    return testObj.Environmental_doable === false;
  }
  return testObj.product_doable === false;
}

export function isTestNameNotDoableForSide(testName, testInfoMap, sampleCategory) {
  if (!testName || !testInfoMap?.get) return false;
  return isTestNotDoableForSide(testInfoMap.get(testName), sampleCategory);
}

export function sampleHasNotDoableTest(sample, testInfoMap, sampleCategory) {
  const names = Array.isArray(sample?.test_list) ? sample.test_list : [];
  return names.some((name) => {
    const testName = typeof name === "string" ? name : name?.Test;
    return isTestNameNotDoableForSide(testName, testInfoMap, sampleCategory);
  });
}

export function notDoableTestMessage(sampleCategory) {
  return sampleCategory === "environment"
    ? "Remove tests that cannot be performed on environment samples."
    : "Remove tests that cannot be performed on product samples.";
}

export function getLinkedFieldsForCategory(category, { link_report, link_pattern_fields } = {}) {
  if (category === "environment") return link_pattern_fields ?? [];
  if (category === PRODUCT_ENVIRONMENT_CATEGORY) {
    return [...(link_report ?? []), ...(link_pattern_fields ?? [])];
  }
  return link_report ?? [];
}

/** productfields / environmentfields: fixed_values fields are not valid per-sample composite identifiers */
export function isDropdownSampleField(info) {
  const type = info?.field_format?.type;
  return typeof type === "string" && type.toLowerCase() === "fixed_values";
}

export function filterCompositeIdentifiers(identifier = [], sampleFieldsInfo = {}) {
  if (!Array.isArray(identifier)) return [];
  return identifier.filter((id) => !isDropdownSampleField(sampleFieldsInfo[id]));
}

/**
 * Limit no. of characters in text
 * @param {String} text Original text
 * @param {Number} maxLength maximum length
 * @returns trimmed text
 */
export const limitTextLength = (text, maxLength) => {
  if (!text) return "";
  if (!maxLength) return text;
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

/**
 * Formats the date to render
 * @param {*} date date string
 * @returns formatted date
 */
export const getFormattedDetailsDate = (date) => {
  if (!date || !date?.trim()) return "";
  return moment(new Date(convertUtcToLocal(date))).format("DD MMM YYYY | hh:mm a");
};

/**
 * Create sub sample id by replacing placeholder value
 * @param {string} sampleId sample id with placeholder
 * @param {object} fields fields to replace
 * @param {number} index index of splitted value
 * @returns sub sample id
 */
const createSubSampleId = (sampleId, fields, index) => {
  let id = sampleId;
  Object.keys(fields).forEach((key) => {
    const value = fields[key]?.split(COMPOSITE_FIELD_DELIMITER)[index];
    id = id.replace(`#${key}#`, value);
  });
  return id;
};

/**
 * split samples into different sample by composite sample wise
 * @param {object} sample sample
 * @param {number} counter counter
 * @returns sample splitted composite sample wise
 */
export const splitSamples = (sample, counter) => {
  const totalSamples = sample.num_composite_samples;
  if (sample.composite_fieldnames.length === 0) {
    return Array(totalSamples).fill({
      ...sample,
      sub_sample_id: sample.sample_id,
      sample_id_counter: counter,
    });
  }

  let sampleId = sample.sample_id;
  sample.composite_fieldnames.forEach((field) => {
    const value = sample[field];
    sampleId = sampleId.replace(value, `#${field}#`);
  });

  const samples = [];
  [...Array(totalSamples)].forEach((_, index) => {
    const fields = pick(sample, sample.composite_fieldnames);
    const sampleCopy = {
      ...sample,
      sub_sample_id: createSubSampleId(sampleId, fields, index),
      sample_id_counter: counter,
    };
    samples.push(sampleCopy);
  });
  return samples;
};
